import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Icon, Message, Modal } from 'semantic-ui-react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns-tz';

import { StyledAddButton } from 'common/components/button/StyledButton';
import LoadingSpinner from 'common/components/LoadingSpinner';
import PositionedModal from 'common/components/PositionedModal';
import StatusLabel from 'common/components/StatusLabel';
import { PageContent, PageHeader, PageSection } from 'common/styles/page';
import DoughnutGauge from 'areas/dashboard/components/DoughnutGauge';
import {
  Table as StyledTable,
  TableBody,
  TableCell,
  TableRow as StyledTableRow,
} from 'common/components/Table';
import { TopBarContext } from 'common/layouts/AuthLayout/TopBarContext';
import TopBarControls from 'common/layouts/AuthLayout/TopBarAssessmentPeriod';
import {
  getCurrentOrganization,
  getCurrentOrganizationTimezone,
  getAssessmentPeriods,
  getSelectedAssessmentPeriod,
  getSelectedAssessmentPeriodId,
  isCurrentUserSuperUser,
} from 'common/selectors/auth';
import { useDispatch, useSelector } from 'react-redux';
import { useToasts } from 'common/toast';
import userContextSlice from 'common/slices/userContextSlice';
import { useGetAllTeamsQuery } from 'areas/team-management/api/teamApi';
import {
  ASSESSMENT_PERIOD_CLOSED,
  ASSESSMENT_PERIOD_IN_PROGRESS,
  ASSESSMENT_PERIOD_OPEN,
} from 'areas/assessment-schedule/constants';

import {
  PulseSurvey,
  PulseSurveyStructure,
  AssessmentQuestionSetOption,
  useDeletePulseSurveyStructureMutation,
  useDeletePulseSurveyMutation,
  useGetPulseParticipantsQuery,
  useListPulseQuestionSetsQuery,
  useListPulseSurveyStructuresQuery,
  useUpdatePulseSurveyMutation,
} from '../api/pulseSurveyPortalApi';
import PulseSurveyFormModal, { PulseSurveyFormSubmitRequest } from './PulseSurveyFormModal';

type PulseSurveyStructureGroup = {
  structureId: number;
  structure: PulseSurveyStructure;
  name: string;
  assessmentQuestionSetTitle?: string;
  firstStartDateTime?: string;
  firstEndDateTime?: string;
  reminderEnabled?: boolean;
  isActive?: boolean;
  children: PulseSurvey[];
};

const statusLabel = (status?: number) => {
  if (status === 1) return 'Scheduled';
  if (status === 2) return 'In Progress';
  if (status === 3) return 'Closed';
  return 'Unknown';
};

const statusColor = (status?: number) => {
  if (status === 1) return '#9A9A9A';
  if (status === 2) return '#1976d2';
  if (status === 3) return '#2e7d32';
  return '#9e9e9e';
};

const structureRecurrencePhrase = (structure: PulseSurveyStructure) => {
  if (structure.recurrence_type !== 2) {
    return 'Once';
  }

  const rule = (structure.recurrence_rule || '').toUpperCase();
  if (rule.includes('FREQ=WEEKLY') && rule.includes('INTERVAL=2')) return 'Every 2 weeks';
  if (rule.includes('FREQ=DAILY')) return 'Daily';
  if (rule.includes('FREQ=WEEKLY')) return 'Weekly';
  if (rule.includes('FREQ=MONTHLY') && rule.includes('INTERVAL=3')) return 'Quarterly';
  if (rule.includes('FREQ=MONTHLY')) return 'Monthly';
  if (rule.includes('FREQ=YEARLY')) return 'Yearly';
  return 'On a recurring basis';
};

const getMostRecentOpenPeriod = (assessmentPeriods: any[]) => {
  if (!assessmentPeriods?.length) return undefined;

  const openPeriods = assessmentPeriods
    .filter(
      (period) => period?.status === ASSESSMENT_PERIOD_OPEN || period?.status === ASSESSMENT_PERIOD_IN_PROGRESS
    )
    .sort(
      (a, b) =>
        new Date(b.start_date_time || b.startDateTime || 0).getTime() -
        new Date(a.start_date_time || a.startDateTime || 0).getTime()
    );

  return openPeriods[0] || assessmentPeriods[0];
};

const getScheduleDisplayName = (survey: PulseSurvey) =>
  survey.start_date_time
    ? format(new Date(survey.start_date_time), 'MMM d, yyyy')
    : 'Pulse Survey';

const PulseSurveyNameCellContent: React.FC<{ survey: PulseSurvey }> = ({ survey }) => {
  const { data } = useGetPulseParticipantsQuery(survey.id ?? 0, { skip: !survey.id });

  const invited = data?.participation.invited ?? 0;
  const responses = data?.participation.completed ?? 0;
  const responseRate = data?.participation.response_rate ?? 0;
  const displayName = getScheduleDisplayName(survey);

  return (
    <SurveyNameCell>
      <DoughnutGauge complete={responseRate * 100} percent={true} size="tiny" />
      <SurveyNameText>
        <SurveyTitle>{displayName}</SurveyTitle>
        <SurveyMeta>
          {invited} invitation{invited === 1 ? '' : 's'} • {responses} response{responses === 1 ? '' : 's'}
        </SurveyMeta>
      </SurveyNameText>
    </SurveyNameCell>
  );
};

const PulseSurveyOverview: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const { setTopBarContent } = useContext(TopBarContext);
  const org = useSelector(getCurrentOrganization) as any;
  const assessmentPeriods = (useSelector(getAssessmentPeriods) as any[]) || [];
  const selectedAssessmentPeriod = useSelector(getSelectedAssessmentPeriod) as any;
  const selectedAssessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);
  const isSuperUser = useSelector(isCurrentUserSuperUser);
  const orgTimezone = useSelector(getCurrentOrganizationTimezone) as string | undefined;

  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState<PulseSurvey | null>(null);
  const [deleteTargetStructure, setDeleteTargetStructure] = useState<PulseSurveyStructure | null>(null);
  const [deleteTargetSurvey, setDeleteTargetSurvey] = useState<PulseSurvey | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const { data: pulseSurveyStructures = [], isLoading, error, refetch } = useListPulseSurveyStructuresQuery();
  const { data: questionSets = [] } = useListPulseQuestionSetsQuery();
  const { data: teamsData } = useGetAllTeamsQuery(undefined, { skip: !org?.id });
  const [updatePulseSurvey, { isLoading: isUpdating }] = useUpdatePulseSurveyMutation();
  const [deletePulseSurveyStructure, { isLoading: isDeletingStructure }] = useDeletePulseSurveyStructureMutation();
  const [deletePulseSurvey, { isLoading: isDeleting }] = useDeletePulseSurveyMutation();
  const isSubmitting = isUpdating;

  useEffect(() => {
    setTopBarContent(
      <TopBarControls
        status={[ASSESSMENT_PERIOD_IN_PROGRESS, ASSESSMENT_PERIOD_CLOSED, ASSESSMENT_PERIOD_OPEN]}
      />
    );

    return () => setTopBarContent(null);
  }, [setTopBarContent, org?.id]);

  useEffect(() => {
    if (selectedAssessmentPeriodId || assessmentPeriods.length === 0) {
      return;
    }

    const fallbackPeriod = getMostRecentOpenPeriod(assessmentPeriods) ?? assessmentPeriods[0];
    if (!fallbackPeriod) {
      return;
    }

    dispatch(
      userContextSlice.actions.setSelectedAssessmentPeriod({
        selectedAssessmentPeriod: fallbackPeriod,
        assessmentPeriods,
      })
    );
  }, [selectedAssessmentPeriodId, assessmentPeriods, dispatch]);

  const filteredPulseSurveyStructures = useMemo(() => {
    const getPeriodCloseTimestamp = (period: any): number | undefined => {
      const rawCloseDate = period?.endDateTime || period?.end_date_time;
      if (!rawCloseDate) {
        return undefined;
      }

      const ts = new Date(rawCloseDate).getTime();
      return Number.isFinite(ts) ? ts : undefined;
    };

    const selectedPeriodFromList =
      assessmentPeriods.find((period: any) => period.id === selectedAssessmentPeriodId) || selectedAssessmentPeriod;

    const selectedCloseTimestamp = getPeriodCloseTimestamp(selectedPeriodFromList);
    if (selectedCloseTimestamp === undefined) {
      return pulseSurveyStructures;
    }

    const nextCloseTimestamp = assessmentPeriods
      .map((period: any) => getPeriodCloseTimestamp(period))
      .filter((timestamp): timestamp is number => timestamp !== undefined && timestamp > selectedCloseTimestamp)
      .sort((a, b) => a - b)[0];

    return pulseSurveyStructures.filter((structure) => {
      const anchorStart = structure.first_start_date_time || structure.schedules?.[0]?.start_date_time;
      const startTimestamp = anchorStart ? new Date(anchorStart).getTime() : Number.NEGATIVE_INFINITY;

      if (!Number.isFinite(startTimestamp) || startTimestamp <= selectedCloseTimestamp) {
        return false;
      }

      if (nextCloseTimestamp !== undefined && startTimestamp >= nextCloseTimestamp) {
        return false;
      }

      return true;
    });
  }, [pulseSurveyStructures, assessmentPeriods, selectedAssessmentPeriodId, selectedAssessmentPeriod]);

  const groupedPulseSurveyStructures = useMemo<PulseSurveyStructureGroup[]>(() => {
    const toTimestamp = (value?: string) => {
      const timestamp = value ? new Date(value).getTime() : Number.NEGATIVE_INFINITY;
      return Number.isFinite(timestamp) ? timestamp : Number.NEGATIVE_INFINITY;
    };

    const result: PulseSurveyStructureGroup[] = filteredPulseSurveyStructures.map((structure: PulseSurveyStructure) => ({
      structureId: structure.id,
      structure,
      name: structure.name,
      assessmentQuestionSetTitle: structure.assessment_question_set_title,
      firstStartDateTime: structure.first_start_date_time,
      firstEndDateTime: structure.first_end_date_time,
      reminderEnabled: structure.reminder_enabled,
      isActive: structure.is_active,
      children: [...(structure.schedules || [])],
    }));

    result.forEach((group) => {
      group.children.sort((a, b) => toTimestamp(a.start_date_time) - toTimestamp(b.start_date_time));
    });

    result.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

    return result;
  }, [filteredPulseSurveyStructures]);

  const openCreate = () => {
    setEditingSurvey(null);
    navigate('/app/pulse-surveys/structures/new');
  };

  const openEdit = (survey: PulseSurvey) => {
    setEditingSurvey(survey);
    setIsScheduleModalOpen(true);
  };

  const openEditStructure = (structure: PulseSurveyStructure) => {
    navigate(`/app/pulse-surveys/structures/${structure.id}/edit`);
  };

  const closeScheduleModal = () => {
    setIsScheduleModalOpen(false);
    setEditingSurvey(null);
  };

  const onSubmitSchedule = async ({ payload }: PulseSurveyFormSubmitRequest) => {
    if (!editingSurvey?.id) return;

    await updatePulseSurvey({
      id: editingSurvey.id,
      payload,
    }).unwrap();
    addToast('Schedule instance updated.', { appearance: 'success', autoDismiss: true });
    await refetch();
  };

  const toggleReminders = async (survey: PulseSurvey) => {
    const surveyId = survey.id;
    if (surveyId === undefined) return;
    setTogglingId(surveyId);
    const enabled = !survey.reminder_enabled;
    const scheduleLabel = getScheduleDisplayName(survey);
    try {
      await updatePulseSurvey({ id: surveyId, payload: { reminder_enabled: enabled } }).unwrap();
      addToast(`Reminders ${enabled ? 'enabled' : 'disabled'} for ${scheduleLabel}.`, {
        appearance: 'success',
        autoDismiss: true,
      });
    } catch (err: any) {
      const detail = err?.data?.detail || err?.data?.error || 'Failed to update reminders.';
      addToast(String(detail), { appearance: 'error', autoDismiss: true });
    } finally {
      setTogglingId(null);
    }
  };

  const onDelete = (survey: PulseSurvey) => {
    setDeleteTargetSurvey(survey);
  };

  const onDeleteStructure = (structure: PulseSurveyStructure) => {
    setDeleteTargetStructure(structure);
  };

  const canDeleteSurvey = (survey: PulseSurvey) =>
    survey.status === 1 || Boolean(isSuperUser);

  const getDeleteWarningText = (survey: PulseSurvey | null) => {
    if (!survey) return null;
    if (survey.status === 2) {
      return 'All invitation links will become invalid and survey responses will be permanently deleted.';
    }
    if (survey.status === 3) {
      return 'Survey responses will be permanently deleted!';
    }
    return null;
  };

  const confirmDelete = async () => {
    if (!deleteTargetSurvey?.id) {
      setDeleteTargetSurvey(null);
      return;
    }

    try {
      await deletePulseSurvey({ id: deleteTargetSurvey.id }).unwrap();
      addToast('Pulse survey deleted.', { appearance: 'success', autoDismiss: true });
      setDeleteTargetSurvey(null);
      refetch();
    } catch (err: any) {
      const detail = err?.data?.detail || err?.data?.error || 'Failed to delete pulse survey.';
      addToast(String(detail), { appearance: 'error', autoDismiss: true });
    }
  };

  const confirmDeleteStructure = async () => {
    if (!deleteTargetStructure?.id) {
      setDeleteTargetStructure(null);
      return;
    }

    try {
      await deletePulseSurveyStructure({ id: deleteTargetStructure.id }).unwrap();
      addToast('Pulse survey deleted.', { appearance: 'success', autoDismiss: true });
      setDeleteTargetStructure(null);
      refetch();
    } catch (err: any) {
      const detail = err?.data?.detail || err?.data?.error || 'Failed to delete pulse survey.';
      addToast(String(detail), { appearance: 'error', autoDismiss: true });
    }
  };

  return (
    <PageContent data-testid="pulse-surveys-page">
      <PageHeader
        title="Pulse Surveys"
        controls={
          <StyledAddButton
            label="New Pulse Survey"
            width="180px"
            onClick={openCreate}
          />
        }
      />

      <PageSection>
        {error && <Message negative content="Failed to load pulse surveys." />}

        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <GroupedTables>
            {groupedPulseSurveyStructures.map((group) => (
              <StructureGroup key={`structure-${group.structureId}`}>
                <StyledTable>
                  <TableBody>
                    <StructureRow
                      $selectable
                      onClick={() => openEditStructure(group.structure)}
                    >
                      <TableCell $firstcol colSpan="5">
                        <StructureNameCell>
                          <StructureTitleWrap>
                            <Icon name="sitemap" color="grey" className="structure-icon" />
                            <StructureName>{group.name}</StructureName>
                          </StructureTitleWrap>
                          <StructureFacts>
                            {group.isActive ? (
                              <StructureFact>
                                <Icon name="recycle" className="recurrence-icon" />
                                <StructureFactEmphasis>{structureRecurrencePhrase(group.structure)}</StructureFactEmphasis>
                              </StructureFact>
                            ) : (
                              <StructureFact>Inactive</StructureFact>
                            )}
                          </StructureFacts>
                        </StructureNameCell>
                      </TableCell>
                      <TableCell justify="center">
                        <StructureActionsControl>
                          <Dropdown
                            button
                            icon="cog"
                            floating
                            className="icon structure-actions-menu"
                            direction="left"
                            aria-label={`Structure actions for ${group.name}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Dropdown.Menu>
                              <Dropdown.Item
                                icon="write"
                                text="Edit Pulse Survey"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEditStructure(group.structure);
                                }}
                              />
                              <Dropdown.Item
                                icon="trash"
                                text="Delete Pulse Survey"
                                disabled={isDeletingStructure}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDeleteStructure(group.structure);
                                }}
                              />
                            </Dropdown.Menu>
                          </Dropdown>
                        </StructureActionsControl>
                      </TableCell>
                    </StructureRow>

                    {group.children.length > 0 && (
                      <ScheduleHeaderRow>
                        <ScheduleHeaderCell $firstcol>Pulse Survey</ScheduleHeaderCell>
                        <ScheduleHeaderCell>Start</ScheduleHeaderCell>
                        <ScheduleHeaderCell>End</ScheduleHeaderCell>
                        <ScheduleHeaderCell>Status</ScheduleHeaderCell>
                        <ScheduleHeaderCell justify="center">Reminders</ScheduleHeaderCell>
                        <ScheduleHeaderCell justify="center">Actions</ScheduleHeaderCell>
                      </ScheduleHeaderRow>
                    )}

                    {group.children.map((row: PulseSurvey) => (
                      <PulseSurveyRow
                        $selectable
                        key={row.id}
                        onClick={() => navigate(`/app/pulse-surveys/${row.id}`)}
                      >
                        <TableCell $firstcol>
                          <ChildNameCell>
                            <PulseSurveyNameCellContent survey={row} />
                          </ChildNameCell>
                        </TableCell>
                        <TableCell>
                          <DatePrimary>{format(new Date(row.start_date_time), 'MMM. d, yyyy (EEE)')}</DatePrimary>
                          <br />
                          <DateSecondary>{format(new Date(row.start_date_time), 'h:mm a z')}</DateSecondary>
                        </TableCell>
                        <TableCell>
                          <DatePrimary>{format(new Date(row.end_date_time), 'MMM. d, yyyy (EEE)')}</DatePrimary>
                          <br />
                          <DateSecondary>{format(new Date(row.end_date_time), 'h:mm a z')}</DateSecondary>
                        </TableCell>
                        <TableCell>
                          <StatusLabel color={statusColor(row.status)}>{statusLabel(row.status)}</StatusLabel>
                        </TableCell>
                        <TableCell justify="center">
                          <ReminderControl>
                            <ReminderToggle
                              type="button"
                              aria-pressed={Boolean(row.reminder_enabled)}
                              aria-label={`${row.reminder_enabled ? 'Disable' : 'Enable'} reminders for ${getScheduleDisplayName(row)}`}
                              title={row.reminder_enabled ? 'Reminders enabled (click to disable)' : 'Reminders disabled (click to enable)'}
                              disabled={togglingId === row.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleReminders(row);
                              }}
                            >
                              <Icon
                                name={row.reminder_enabled ? 'bell' : 'bell slash'}
                                color={row.reminder_enabled ? 'green' : 'grey'}
                                size="large"
                              />
                            </ReminderToggle>
                            <ReminderStateLabel $enabled={Boolean(row.reminder_enabled)}>
                              {row.reminder_enabled ? 'On' : 'Off'}
                            </ReminderStateLabel>
                          </ReminderControl>
                        </TableCell>
                        <TableCell justify="center">
                          <ScheduleActionsControl>
                            <Dropdown
                              button
                              icon="ellipsis vertical"
                              floating
                              className="icon schedule-actions-menu"
                              direction="left"
                              aria-label={`Schedule actions for ${getScheduleDisplayName(row)}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Dropdown.Menu>
                                <Dropdown.Item
                                  icon="history"
                                  text="View Details"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/app/pulse-surveys/${row.id}`);
                                  }}
                                />
                                <Dropdown.Item
                                  icon="send"
                                  text="Remind Team Participants"
                                  disabled
                                />
                                <Dropdown.Item
                                  icon="write"
                                  text="Edit Schedule"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEdit(row);
                                  }}
                                />
                                <Dropdown.Item
                                  icon="trash"
                                  text="Delete Schedule"
                                  disabled={isDeleting || !canDeleteSurvey(row)}
                                  title={
                                    canDeleteSurvey(row)
                                      ? undefined
                                      : 'Only superusers can delete in-progress or closed pulse surveys.'
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(row);
                                  }}
                                />
                              </Dropdown.Menu>
                            </Dropdown>
                          </ScheduleActionsControl>
                        </TableCell>
                      </PulseSurveyRow>
                    ))}
                  </TableBody>
                </StyledTable>
              </StructureGroup>
            ))}

            {groupedPulseSurveyStructures.length === 0 && (
              <StyledTable>
                <TableBody>
                  <StyledTableRow>
                    <TableCell colSpan="6" justify="center">
                      No pulse surveys found after the selected assessment period close date.
                    </TableCell>
                  </StyledTableRow>
                </TableBody>
              </StyledTable>
            )}
          </GroupedTables>
        )}
      </PageSection>

      <PulseSurveyFormModal
        open={isScheduleModalOpen}
        survey={editingSurvey}
        questionSets={questionSets as AssessmentQuestionSetOption[]}
        teams={(teamsData?.results ?? []).map((team) => ({ id: team.id, name: team.name }))}
        orgName={org?.name || org?.organizationName || org?.organization_name}
        isSubmitting={isSubmitting}
        orgTimezone={orgTimezone}
        defaultReminderSchedule={org?.defaultReminderSchedule ?? org?.default_reminder_schedule ?? '0 9 * * 1,3,5'}
        onClose={closeScheduleModal}
        onSubmit={onSubmitSchedule}
      />

      <PositionedModal
        open={Boolean(deleteTargetStructure)}
        onClose={() => setDeleteTargetStructure(null)}
        size="small"
      >
        <Modal.Header>Delete Schedule</Modal.Header>
        <Modal.Content>
          {`Are you sure you want to delete "${deleteTargetStructure?.name || 'this pulse survey'}"?`}
          {(deleteTargetStructure?.schedules?.length || 0) > 0 && (
            <DeleteWarning>
              <Icon name="warning sign" color="orange" />
              <DeleteWarningText>
                {`This also deletes ${deleteTargetStructure?.schedules?.length} schedule instance${deleteTargetStructure?.schedules?.length === 1 ? '' : 's'}.`}
              </DeleteWarningText>
            </DeleteWarning>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setDeleteTargetStructure(null)} disabled={isDeletingStructure}>Cancel</Button>
          <Button negative onClick={confirmDeleteStructure} loading={isDeletingStructure} disabled={isDeletingStructure}>Delete</Button>
        </Modal.Actions>
      </PositionedModal>

      <PositionedModal
        open={Boolean(deleteTargetSurvey)}
        onClose={() => setDeleteTargetSurvey(null)}
        size="small"
      >
        <Modal.Header>Delete Schedule</Modal.Header>
        <Modal.Content>
          {`Are you sure you want to delete "${deleteTargetSurvey ? getScheduleDisplayName(deleteTargetSurvey) : 'this pulse survey'}"?`}
          {getDeleteWarningText(deleteTargetSurvey) && (
            <DeleteWarning>
              <Icon name="warning sign" color="orange" />
              <DeleteWarningText>{getDeleteWarningText(deleteTargetSurvey)}</DeleteWarningText>
            </DeleteWarning>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setDeleteTargetSurvey(null)} disabled={isDeleting}>Cancel</Button>
          <Button negative onClick={confirmDelete} loading={isDeleting} disabled={isDeleting}>Delete</Button>
        </Modal.Actions>
      </PositionedModal>
    </PageContent>
  );
};

export default PulseSurveyOverview;

const GroupedTables = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StructureGroup = styled.div`
  width: 100%;
`;

const PulseSurveyRow = styled(StyledTableRow)<{ $selectable?: boolean }>`
  height: auto;
`;

const ScheduleHeaderRow = styled(StyledTableRow)`
  background: #f8fafc;
`;

const ScheduleHeaderCell = styled(TableCell)`
  font-family: 'Poppins', sans-serif;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.1;
  letter-spacing: 0.02em;
  color: #667085;
  padding-top: 4px !important;
  padding-bottom: 4px !important;
`;

const StructureRow = styled(StyledTableRow)<{ $selectable?: boolean }>`
  background: linear-gradient(90deg, #e9f2ff 0%, #f4f8ff 100%);

  td {
    border-bottom-color: #d7e4f5 !important;
  }
`;

const StructureNameCell = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  width: 100%;

  & > i.icon {
    margin: 0 !important;
    line-height: 1;
    display: inline-flex;
    align-items: center;
  }
`;

const StructureTitleWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 20px;

  & > i.icon {
    margin: 0 !important;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    align-self: center;
  }

  & > i.structure-icon.icon {
    font-size: 19px !important;
    color: #2a4f80 !important;
  }
`;

const StructureName = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 17px;
  font-weight: 600;
  line-height: 1.2;
  color: #13263f;
`;

const StructureFacts = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 14px;
  align-items: center;
  flex-wrap: wrap;
`;

const StructureFact = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  line-height: 1;
  color: #475467;

  & > i.recurrence-icon.icon {
    display: inline-flex;
    align-items: center;
    line-height: 1;
    color: #5f7ea8 !important;
    font-size: 15px !important;
    margin: 0 !important;
  }
`;

const StructureFactEmphasis = styled.span`
  display: inline-flex;
  align-items: center;
  line-height: 1;
  color: #111827;
  font-weight: 500;
`;

const ChildNameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 0;
`;

const ReminderControl = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
`;

const ReminderToggle = styled.button`
  border: none;
  background: none;
  padding: 6px;
  border-radius: 8px;
  cursor: pointer;
  line-height: 0;

  &:hover:not(:disabled) {
    background: #f0f2f5;
  }

  &:focus-visible {
    outline: 2px solid #2d70e2;
  }

  &:disabled {
    cursor: progress;
    opacity: 0.5;
  }

  i.icon {
    margin: 0;
  }
`;

const ReminderStateLabel = styled.span<{ $enabled: boolean }>`
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: ${({ $enabled }) => ($enabled ? '#166534' : '#6b7280')};
`;

const StructureActionsControl = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 30px;

  .structure-actions-menu.ui.button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 4px;
    background: transparent;
    border: 1px solid transparent;
    box-shadow: none;
    padding: 0;
    line-height: 1;
    transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
  }

  .structure-actions-menu.ui.button > i.icon {
    margin: 0;
    line-height: 1;
  }

  .structure-actions-menu.ui.button:hover,
  .structure-actions-menu.ui.button:focus-visible {
    background: #f8fafc;
    border-color: #98a2b3;
  }
`;

const ScheduleActionsControl = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 30px;

  .schedule-actions-menu.ui.button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 4px;
    background: transparent;
    border: 1px solid transparent;
    box-shadow: none;
    padding: 0;
    line-height: 1;
    transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
  }

  .schedule-actions-menu.ui.button > i.icon {
    margin: 0;
    line-height: 1;
  }

  .schedule-actions-menu.ui.button:hover,
  .schedule-actions-menu.ui.button:focus-visible {
    background: #f8fafc;
    border-color: #98a2b3;
  }
`;

const SurveyNameCell = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const SurveyNameText = styled.div`
  display: flex;
  flex-direction: column;
`;

const SurveyTitle = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.25;
`;

const SurveyMeta = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  color: #666;
  margin-top: 2px;
`;

const DatePrimary = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
`;

const DateSecondary = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
`;

const DeleteWarning = styled.div`
  margin-top: 12px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: #b42318;
  padding: 10px 12px;
`;

const DeleteWarningText = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
`;
